import { useState } from 'react';
import { BRAND_IMAGE_ACCEPT, SIGNATURE_IMAGE_MAX_BYTES, realEntityName, type ProfileView, type SignatureStyle } from '@pqrs/contracts';
import { Button } from '@xiltrion/ui/button';
import { CargaArchivos } from '@xiltrion/ui/bloques/carga-archivos';
import { Field as FieldUi, FieldContent, FieldDescription, FieldLabel, FieldTitle } from '@xiltrion/ui/field';
import { Icono } from '@xiltrion/ui/icono';
import { RadioGroup, RadioGroupItem } from '@xiltrion/ui/radio-group';
import { Spinner } from '@xiltrion/ui/spinner';
import { avisar } from '@xiltrion/ui/sonner';
import { formatBytes } from '@xiltrion/ui/use-file-upload';
import { useSession } from '@/app/session';
import { Card, Field } from '@/components/ui';
import { errorMessage } from '@/features/cases/lib';
import { confirm } from '@/features/config/shared';
import { useRemoveSignatureImage, useSignatureImage, useSignatureStyle } from './api';

/** Contenido de un archivo en base64, sin el prefijo `data:`. */
const toBase64 = (file: File) => new Promise<string>((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
  r.onerror = () => reject(r.error ?? new Error('No se pudo leer el archivo'));
  r.readAsDataURL(file);
});

const OPTIONS: Array<{ value: SignatureStyle; title: string; description: string }> = [
  { value: 'TEXT', title: 'Nombre y cargo', description: 'Tu nombre en negrilla, tu cargo y la entidad.' },
  { value: 'IMAGE', title: 'Imagen de mi firma', description: 'Tu firma manuscrita escaneada, sobre tu nombre y cargo.' },
];

/**
 * Cómo firma la persona sus respuestas (tarea «Firmar»): con su nombre y cargo o con la imagen de su firma encima.
 * La firma electrónica (código de la app o del correo) es la misma; esto cambia solo cómo se ve en la carta.
 */
export function SignatureTab({ profile }: { profile: ProfileView }) {
  const { me } = useSession();
  const sig = profile.signature;
  const style = useSignatureStyle();
  const image = useSignatureImage();
  const remove = useRemoveSignatureImage();
  // Elegir «Imagen» sin haberla subido solo muestra dónde subirla: se firma con ella al subirla.
  const [choice, setChoice] = useState<SignatureStyle>(sig.style);
  const [files, setFiles] = useState<File[]>([]);
  const shown = choice === 'IMAGE' || sig.style === 'IMAGE' ? 'IMAGE' : 'TEXT';
  const fail = (e: unknown) => avisar.error('No pudimos guardar tu firma', { description: errorMessage(e) });

  const pick = (value: string) => {
    const next = value as SignatureStyle;
    setChoice(next);
    if (next === sig.style || (next === 'IMAGE' && !sig.image)) return;
    style.mutate(next, { onSuccess: () => avisar.success(next === 'IMAGE' ? 'Firmarás con la imagen de tu firma' : 'Firmarás con tu nombre y cargo'), onError: (e) => { setChoice(sig.style); fail(e); } });
  };
  const upload = async (list: File[]) => {
    const f = list.at(-1);
    setFiles(f ? [f] : []);
    if (!f) return;
    image.mutate({ fileName: f.name, base64: await toBase64(f) }, { onSuccess: () => { setFiles([]); setChoice('IMAGE'); avisar.success('Imagen de tu firma guardada', { description: 'Tus próximas respuestas firmadas la llevarán.' }); }, onError: (e) => { setFiles([]); fail(e); } });
  };
  const ask = async () => {
    if (await confirm({ header: 'Quitar la imagen de tu firma', message: 'Tus próximas respuestas se firmarán con tu nombre y cargo. Las que ya firmaste no cambian.', acceptLabel: 'Quitar', danger: true })) {
      remove.mutate(undefined, { onSuccess: () => { setChoice('TEXT'); avisar.success('Firmarás con tu nombre y cargo'); }, onError: fail });
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-2 items-start">
      <Card title="Cómo firmas tus respuestas">
        <div className="flex flex-col gap-4">
          <p className="text-sm m-0 text-muted-foreground">Así aparece tu firma al pie de las respuestas que firmas. La firma electrónica es la misma en los dos casos: la confirmas con el código de tu app autenticadora o del correo.</p>
          <RadioGroup value={shown} onValueChange={pick} disabled={style.isPending} className="gap-2" aria-label="Cómo firmas tus respuestas">
            {OPTIONS.map((o) => (
              <FieldLabel key={o.value} htmlFor={`sig-${o.value}`} className="cursor-pointer">
                <FieldUi orientation="horizontal">
                  <FieldContent>
                    <FieldTitle>{o.title}</FieldTitle>
                    <FieldDescription>{o.description}</FieldDescription>
                  </FieldContent>
                  <RadioGroupItem id={`sig-${o.value}`} value={o.value} />
                </FieldUi>
              </FieldLabel>
            ))}
          </RadioGroup>
          {shown === 'IMAGE' && (
            <Field label="Imagen de tu firma" hint="Firma en una hoja blanca con tinta oscura, escanéala o tómale una foto y recórtala al borde de la firma. Mejor en PNG con fondo transparente.">
              {sig.image && (
                <div className="flex flex-col gap-2 rounded-md border border-border bg-muted p-3">
                  <img src={sig.image.dataUrl} alt="Imagen de tu firma" className="h-16 w-auto max-w-full self-start object-contain" />
                  <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span className="tnum truncate">{sig.image.fileName} · {sig.image.width} × {sig.image.height} px</span>
                    <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" disabled={remove.isPending} onClick={() => void ask()}>{remove.isPending ? <Spinner /> : <Icono nombre="mdi:trash-can-outline" />} Quitar</Button>
                  </div>
                </div>
              )}
              <CargaArchivos archivos={files} onArchivosChange={(l) => void upload(l)} accept={BRAND_IMAGE_ACCEPT} maxTamano={SIGNATURE_IMAGE_MAX_BYTES} maxArchivos={1} deshabilitado={image.isPending}
                compacto={!!sig.image} titulo={image.isPending ? 'Guardando…' : sig.image ? 'Reemplazar la imagen' : 'Arrastra aquí la imagen de tu firma'} textoElegir="elígela"
                textoAyuda={`PNG o JPG, hasta ${formatBytes(SIGNATURE_IMAGE_MAX_BYTES)}`} aria-label="Cargar la imagen de tu firma" />
            </Field>
          )}
        </div>
      </Card>
      <Card title="Así se verá">
        <div className="rounded-md border border-border bg-background p-4 text-sm">
          <p className="m-0 text-xs font-semibold">Documento firmado electrónicamente</p>
          <p className="m-0 text-xs text-muted-foreground tnum">Código de verificación</p>
          {sig.style === 'IMAGE' && sig.image
            ? <img src={sig.image.dataUrl} alt="" aria-hidden className="mt-2 h-14 w-auto max-w-48 object-contain object-left-bottom" />
            : <div className="h-6" aria-hidden />}
          <p className="m-0 font-semibold uppercase">{profile.displayName}</p>
          {profile.jobTitle && <p className="m-0 uppercase">{profile.jobTitle}</p>}
          <p className="m-0 uppercase">{realEntityName(me?.entity.name) ?? '—'}</p>
        </div>
        {!profile.jobTitle && <p className="text-xs text-muted-foreground m-0 mt-2">Aún no tienes cargo: lo registra el administrador de la entidad en la suite Datalog.</p>}
      </Card>
    </div>
  );
}
